import React, { useEffect, useMemo, useState } from 'react';
import './wordmonster.css';
import AudioPlayer from '../AudioPlayer';
import wordMonsterTheme from '../../sound/wordmonster/theme.mp3';

export default function WordMonsterPlayer({ socket, sessionId, playerName, isMusicMuted = false }) {
  const [state, setState] = useState({
    phase: 'lobby',
    round: 1,
    prompt: null,
    cravingMonster: null,
    roundRuleText: '',
    roundEndsAt: null,
    recentEvents: [],
    scoreboard: [],
    avatarChoices: [],
  });
  const [word, setWord] = useState('');
  const [submitFeedback, setSubmitFeedback] = useState('');
  const [timeLeftMs, setTimeLeftMs] = useState(0);

  useEffect(() => {
    if (!socket) return undefined;

    const update = (incoming) => setState((prev) => ({ ...prev, ...incoming }));
    const onAck = (payload) => {
      if (!payload.valid) {
        setSubmitFeedback('Not found as a synonym or antonym for this target word.');
        return;
      }
      if (payload.delta > 0) {
        setSubmitFeedback(`Accepted: +${payload.delta}`);
      } else if (payload.delta < 0) {
        setSubmitFeedback(`Duplicate penalty: ${payload.delta}`);
      } else {
        setSubmitFeedback('Duplicate: no points this round.');
      }
    };

    socket.on('wordMonsterState', update);
    socket.on('wordMonsterSubmitAck', onAck);
    socket.emit('wordMonsterSyncState', { sessionId });

    return () => {
      socket.off('wordMonsterState', update);
      socket.off('wordMonsterSubmitAck', onAck);
    };
  }, [socket, sessionId]);

  const me = useMemo(
    () => (state.scoreboard || []).find((entry) => entry.name === playerName),
    [state.scoreboard, playerName],
  );

  useEffect(() => {
    if (state.phase !== 'active-round' || !state.roundEndsAt) {
      setTimeLeftMs(0);
      return undefined;
    }

    const tick = () => {
      setTimeLeftMs(Math.max(state.roundEndsAt - Date.now(), 0));
    };

    tick();
    const intervalId = setInterval(tick, 100);
    return () => clearInterval(intervalId);
  }, [state.phase, state.roundEndsAt]);

  const selectAvatar = (avatar) => {
    socket.emit('wordMonsterSelectAvatar', {
      sessionId,
      avatar,
    });
  };

  const submit = () => {
    if (!word.trim()) return;
    socket.emit('wordMonsterSubmitWord', {
      sessionId,
      word,
    });
    setWord('');
  };

  const isThemePlaying = state.phase === 'lobby' || state.phase === 'active-round';

  return (
    <div className="wm-player-shell">
      <AudioPlayer audioSrc={wordMonsterTheme} isPlaying={isThemePlaying} isMuted={isMusicMuted} />
      <div className="wm-player-card">
        <h2>Word Monster</h2>
        <p>{playerName} • Score: {me?.points ?? 0}</p>
        <p>Round {state.round} • {state.phase.replace('-', ' ')}</p>
        {state.prompt && <h3 className="wm-player-target">{state.prompt}</h3>}

        {state.phase === 'lobby' && (
          <div className="wm-avatar-picker">
            {(state.avatarChoices || []).map((avatar) => (
              <button
                key={avatar}
                className={`wm-avatar-choice ${me?.avatar === avatar ? 'selected' : ''}`}
                onClick={() => selectAvatar(avatar)}
              >
                {avatar}
              </button>
            ))}
          </div>
        )}

        {state.phase === 'round-intro' && (
          <div className="wm-player-info">
            <p>{state.roundRuleText}</p>
            {state.round === 3 && (
              <p>
                Craving Monster: {state.cravingMonster === 'synonyms' ? 'Synonym Monster' : 'Antonym Monster'}
              </p>
            )}
            <p>Wait for host to begin the timer.</p>
          </div>
        )}

        {state.phase === 'active-round' && (
          <div className="wm-player-active">
            <p>Type fast: submit as many words as possible in 30 seconds.</p>
            <p>Time Left: {(timeLeftMs / 1000).toFixed(1)}s</p>
            <div className="wm-submit-row">
              <input
                value={word}
                onChange={(e) => setWord(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') submit();
                }}
                placeholder="Enter synonym or antonym"
              />
              <button className="wm-primary-button" onClick={submit}>Feed</button>
            </div>
            <div className="wm-feedback">{submitFeedback}</div>
          </div>
        )}

        {(state.phase === 'round-summary' || state.phase === 'game-over') && (
          <div className="wm-player-info">
            <p>Round ended. Watch host screen for rankings.</p>
          </div>
        )}

        <div className="wm-player-feed">
          {(state.recentEvents || []).slice(-6).reverse().map((event) => (
            <div key={event.id} className="wm-log-item">
              <span>{event.playerAvatar} {event.playerName}: {event.word}</span>
              <strong>{event.delta > 0 ? `+${event.delta}` : event.delta}</strong>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
